/* The mobile portrait order bar delegates to the existing, validated order action. */
(() => {
  const installed = new WeakSet();

  function install(root) {
    if (!root || installed.has(root)) return;
    const order = root.querySelector('[data-uk-order]');
    if (!order) return;
    installed.add(root);

    const dock = document.createElement('div');
    dock.className = 'uk-portrait-order-dock';
    dock.setAttribute('data-uk-order-dock', '');
    dock.setAttribute('role', 'region');
    dock.setAttribute('aria-label', 'Order your portrait');
    dock.hidden = true;
    const inner = document.createElement('div');
    inner.className = 'uk-portrait-order-dock__inner';
    const summary = document.createElement('div');
    summary.className = 'uk-portrait-order-dock__summary';
    const selection = document.createElement('span');
    selection.className = 'uk-portrait-order-dock__selection';
    const price = document.createElement('strong');
    price.className = 'uk-portrait-order-dock__price';
    price.setAttribute('data-uk-dock-price', '');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'uk-portrait-order-dock__button';
    button.setAttribute('data-uk-dock-order', '');
    button.disabled = true;
    const label = document.createElement('span');
    label.textContent = 'Order My Portrait';
    const arrow = document.createElement('span');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    button.append(label, arrow);
    summary.append(selection, price);
    inner.append(summary, button);
    dock.append(inner);
    document.body.append(dock);

    const mobile = window.matchMedia('(max-width: 749px)');
    const abort = new AbortController();
    const observers = [];
    let frame = 0, pending = false, releaseTimer = 0;
    const text = (element, value) => {
      if (element.textContent !== value) element.textContent = value;
    };
    function visible(element) {
      if (!element || element.closest('[hidden], [aria-hidden="true"], [inert]') ||
          (element.tagName === 'DIALOG' && !element.open)) return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    }
    function inViewport(element, top = 0) {
      if (!visible(element)) return false;
      const rect = element.getBoundingClientRect();
      return rect.bottom > top && rect.top < window.innerHeight;
    }
    function keyboardFocused() {
      const active = document.activeElement;
      return Boolean(active?.matches('input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]'));
    }
    function blockedByOverlay() {
      return document.documentElement.hasAttribute('scroll-lock') ||
        document.documentElement.classList.contains('uk-portrait-progress-open') ||
        document.body.matches('.js-drawer-open, .modal-open') ||
        [...document.querySelectorAll('dialog[open], [aria-modal="true"], .gemini-transformer__modal.is-open')].some(visible);
    }
    function sync() {
      if (!root.isConnected) {
        abort.abort();
        observers.forEach(observer => observer.disconnect());
        clearTimeout(releaseTimer);
        dock.remove();
        return;
      }
      const finish = root.querySelector('[data-uk-summary-finish]')?.textContent.trim() || '';
      const choice = root.querySelector('[data-uk-summary-choice]')?.textContent.trim() || '';
      const total = root.querySelector('[data-uk-total]')?.textContent.trim() || '';
      const selectedText = [finish, choice].filter(Boolean).join(' · ');
      text(selection, selectedText);
      text(price, total);
      const accessibleLabel = ['Order My Portrait', selectedText, total].filter(Boolean).join(', ');
      if (button.getAttribute('aria-label') !== accessibleLabel) button.setAttribute('aria-label', accessibleLabel);

      const nativeOrder = document.querySelector('form[id^="AddToCartForm--"] button[name="add"]');
      const ready = root.dataset.view === 'ready' && root.dataset.settled === 'true' &&
        root.dataset.ukBusy !== 'true' && !root.dataset.revealing && visible(root) && visible(order) && Boolean(total);
      const busy = order.disabled || order.getAttribute('aria-disabled') === 'true' || order.getAttribute('aria-busy') === 'true' ||
        nativeOrder?.disabled || nativeOrder?.getAttribute('aria-busy') === 'true' ||
        nativeOrder?.form?.getAttribute('aria-busy') === 'true';
      button.disabled = !ready || Boolean(busy) || pending;
      button.setAttribute('aria-busy', String(pending || Boolean(busy)));

      const header = document.querySelector('.site-header') || document.querySelector('.header-section');
      const headerBottom = visible(header) ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const footer = document.querySelector('footer') || document.querySelector('[role="contentinfo"]');
      const shouldShow = mobile.matches && ready && inViewport(root, headerBottom) &&
        !inViewport(order, headerBottom) && !inViewport(footer) && !keyboardFocused() && !blockedByOverlay();
      dock.hidden = !shouldShow;
      dock.setAttribute('aria-hidden', String(!shouldShow));
      dock.toggleAttribute('inert', !shouldShow);
    }
    function queue() {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; sync(); });
    }
    function listen(target, event, options = {}) {
      target?.addEventListener(event, queue, { ...options, signal: abort.signal });
    }
    function observe(target, options) {
      if (!target) return;
      const observer = new MutationObserver(queue);
      observer.observe(target, options);
      observers.push(observer);
    }

    button.addEventListener('click', () => {
      sync();
      if (dock.hidden || button.disabled || pending) return;
      pending = true;
      sync();
      // All artwork, variant and basket checks remain owned by the main button.
      try { order.click(); } finally {
        releaseTimer = setTimeout(() => { pending = false; queue(); }, 1000);
      }
    }, { signal: abort.signal });
    ['scroll', 'resize', 'pageshow'].forEach(event => listen(window, event, { passive: true }));
    ['scroll', 'resize'].forEach(event => listen(window.visualViewport, event, { passive: true }));
    ['change', 'input', 'uk:portrait-presented', 'uk:portrait-progress-open', 'uk:portrait-progress-close'].forEach(event => listen(root, event));
    ['focusin', 'focusout', 'toggle', 'close', 'dialog:open', 'dialog:close'].forEach(event => listen(document, event, { capture: true }));
    listen(mobile, 'change');
    observe(root, { subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-disabled', 'aria-busy', 'aria-hidden', 'inert', 'data-view', 'data-settled', 'data-revealing', 'data-uk-busy'] });
    observe(document.querySelector('form[id^="AddToCartForm--"]'), { subtree: true, childList: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-busy'] });
    observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'scroll-lock'] });
    observe(document.body, { attributes: true, childList: true, attributeFilter: ['class'] });
    for (const overlay of document.querySelectorAll('dialog, [aria-modal="true"]')) {
      observe(overlay, { attributes: true, attributeFilter: ['open', 'hidden', 'aria-hidden', 'class', 'style'] });
      observe(overlay.parentElement, { attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'class', 'style'] });
    }
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(queue);
      observer.observe(root);
      observers.push(observer);
    }
    queue();
  }

  const start = () => install(document.querySelector('[data-uk-portrait]'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
